import type { ReactNode } from 'react';
import { useLingui, Trans } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';

const PROJECTS_EMAIL = 'projects@worldcoo.com';
const linkClass = 'font-semibold hover:underline';

function ContactLink(props: { children: ReactNode }) {
	return (
		<a href={`mailto:${PROJECTS_EMAIL}`} className={linkClass}>
			{props.children}
		</a>
	);
}

export function useFaqCategories() {
	const { t } = useLingui();

	return [
		{
			id: 'intro',
			title: t`Welcome`,
			items: [
				{
					question: t`Where do I start?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									Hi! Here you will find answers to the main questions you may have about the <strong>NGO Area</strong>{' '}
									and <strong>Worldcoo Plus</strong>.
								</Trans>
							</p>
							<p>
								<Trans>
									Likewise, to guide you during the first months of using these new tools, we have prepared a{' '}
									<strong>usage manual</strong> that explains in detail everything you need to know about using them.
									You will always find it{' '}
									<Link to="/ngos/resources" className={linkClass}>
										available here
									</Link>
									.
								</Trans>
							</p>
						</div>
					),
				},
			],
		},
		{
			id: 'results',
			title: t`Results`,
			items: [
				{
					question: t`How can I get data about my fundraising campaigns?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									The{' '}
									<Link to="/ngos/dashboard" search={{ scope: 'all' }} className={linkClass}>
										Results
									</Link>{' '}
									section shows data from the fundraising campaigns that your organization has activated through
									Worldcoo channels and through the Worldcoo Plus portal, if you are subscribed to the service. At the
									top of the section you will find a summary of the overall figures: total amount raised across
									campaigns, total number of donations received, average donation amount and number of campaigns that
									have been carried out.
								</Trans>
							</p>
							<p>
								<Trans>
									Below that data, we show you the monthly evolution of the amount raised and the number of donations. A
									bit further down, you will also see the campaigns and cities that have raised the most, as well as
									other figures such as the most generous donation, the day with the most donations registered, the
									store with the most donations and the month with the best results.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`Where can I see a history of past campaigns?`,
					answer: (
						<p>
							<Trans>
								At the bottom of the{' '}
								<Link to="/ngos/dashboard" search={{ scope: 'all' }} className={linkClass}>
									Results
								</Link>{' '}
								section you will find a campaign history: a list of every fundraising campaign carried out for your
								organization. The list shows the campaign name, start and end dates, amount raised, which store
								activated the fundraiser, the URL of the campaign information page (the URL of the store's solidarity
								portal, or the URL of the organization's solidarity portal if subscribed to Worldcoo Plus and the
								campaign was launched through it) and its current status: active (receiving donations), funded (already
								finished) or paused (temporarily stopped). You can also download the list in CSV format with the
								"Download" option.
							</Trans>
						</p>
					),
				},
				{
					question: t`How can I get more specific data?`,
					answer: (
						<p>
							<Trans>
								The{' '}
								<Link to="/ngos/dashboard" search={{ scope: 'all' }} className={linkClass}>
									Results
								</Link>{' '}
								section includes several filters that help you obtain more specific figures. You can filter by date,
								campaign name, store where the campaign was active and city. This lets you get specific data for a given
								year, a particular campaign or a specific store. Filters appear both at the top of the section, to
								filter the overall data, and at the bottom, to filter inside the campaign list. In that list you can
								also filter by campaign status: active (receiving donations), funded (already finished) or paused
								(temporarily stopped).
							</Trans>
						</p>
					),
				},
			],
		},
		{
			id: 'projects',
			title: t`Projects`,
			items: [
				{
					question: t`How do I create a new project?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									Go to the{' '}
									<Link to="/ngos/projects" className={linkClass}>
										Projects
									</Link>{' '}
									section and click on "New Project". You will be guided through a step-by-step form: first choose the
									languages in which you want the project to be available. The default language will be the one you use
									to create the project. If you select more languages, the project details will be translated
									automatically into the selected languages and you will be able to review those translations later.
								</Trans>
							</p>
							<p>
								<Trans>
									Next, write the key information about the project (name, context and description, goals and
									activities), then add details such as location, beneficiaries, category and media (cover image, video,
									additional images), and finally configure the last details such as where you want your project to be
									available and the financial goal.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`How do I edit the translations of a project?`,
					answer: (
						<p>
							<Trans>
								Once you have created a project, you can edit the translations into the languages you selected when
								creating it. To do so, select the project and, inside its menu, a sparkle icon with the letters "AI"
								will appear next to the sections that have translations pending review. To edit them, click the icon and
								then the pencil next to each language.
							</Trans>
						</p>
					),
				},
				{
					question: t`Can I edit a project after it is created?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									All project information can be modified after creation by going back to the{' '}
									<Link to="/ngos/projects" className={linkClass}>
										Projects
									</Link>{' '}
									section and selecting the project you want to edit.
								</Trans>
							</p>
							<p>
								<Trans>
									For projects available on the Worldcoo channel, they can be modified at any time, but if we have
									already presented them to partner companies, those companies will have received the version of the
									moment in which we presented it, which may be an older version. In any case, if a company selects your
									project, we will prepare a content proposal that you will need to validate, and at that moment you
									will also be able to update the project's data.
								</Trans>
							</p>
							<p>
								<Trans>
									For projects available on the portals channel, they can always be modified, even while they are
									actively receiving funding.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`How do I delete a project and how do I create a copy?`,
					answer: (
						<p>
							<Trans>
								Go to{' '}
								<Link to="/ngos/projects" className={linkClass}>
									Projects
								</Link>{' '}
								and click on the three dots in the box that shows a summary of the project's basic information. Three
								actions will appear: edit, duplicate, delete. Choose the action you want to perform: if you choose to
								delete it, a message will appear asking you to confirm. If you choose to duplicate it, a copy of the
								project will be created and you will be able to edit it so it differs from the original.
							</Trans>
						</p>
					),
				},
				{
					question: t`How can I get funding for my projects?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									When you create the project, you have to select where you want it to be available so it can receive
									funding through the existing <strong>funding channels</strong>:
								</Trans>
							</p>
							<p>
								<strong>
									<Trans>Worldcoo channel</Trans>
								</strong>
							</p>
							<p>
								<Trans>
									Worldcoo is the channel where every project can be available by default, without needing to activate a
									subscription. If you select this channel, we will present your projects to Worldcoo's partner
									companies so they can choose them to launch micro-donation campaigns. It is usually a long process,
									and we do not know exactly when a company will be interested in helping fund a specific project (there
									are more than 900 organizations interested in receiving funding and a limited number of companies).
								</Trans>
							</p>
							<p>
								<strong>
									<Trans>Worldcoo Plus portals (subscription service)</Trans>
								</strong>
							</p>
							<p>
								<Trans>
									Portals are a funding channel in which a dedicated web page is created for the organization and
									campaigns can be uploaded to start fundraising. A subscription is required to create a portal and run
									a campaign on it. Donors can visit the link and donate to the selected campaign, which can be
									activated immediately after creation and start receiving funding from minute zero.
								</Trans>
							</p>
							<p>
								<Trans>
									At any time you can edit the configuration and add or remove the channels where your projects are
									available.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`How can I change or add a different funding channel for my project?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									If I have a project shared with Worldcoo and I want it to be available on my portal too, I can easily
									link it to the portal, in the following ways:
								</Trans>
							</p>
							<ol className="ml-5 list-decimal space-y-2">
								<li>
									<Trans>
										Select the existing project inside the project list, in the{' '}
										<Link to="/ngos/projects" className={linkClass}>
											Projects
										</Link>{' '}
										section. A menu will open. Select "Funding channels" and select "My portal". Add the details
										requested by the assistant. Save the changes.
									</Trans>
								</li>
								<li>
									<Trans>
										Select{' '}
										<Link to="/ngos/portals" className={linkClass}>
											Worldcoo Plus
										</Link>
										, select the portal and, once inside, click on "Campaigns". Click "New campaign" and "Assign an
										existing project". Choose the project you want to be available on the portal. Add the details
										requested by the assistant. Save the changes.
									</Trans>
								</li>
							</ol>
						</div>
					),
				},
				{
					question: t`What should the project be like to be successful?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<strong>
									<Trans>Worldcoo channel</Trans>
								</strong>
							</p>
							<p>
								<Trans>
									For projects available on the Worldcoo channel, they have to be projects that are sustainable over
									time (that can be funded both now and at any other moment, even years later) because we never know
									when a company will select them: it may be more or less quick. Ideally they should be key projects of
									the organization, around the causes the organization is best known for (non-ideal example: an
									organization known for health projects sends us an environmental project). The decision to make a
									micro-donation at a point of sale is very fast, and it is often much easier to explain and understand
									the project if it relates to a usual cause of the organization so the donor can quickly associate the
									logo and the cause.
								</Trans>
							</p>
							<p>
								<strong>
									<Trans>Worldcoo Plus portals, subscription service</Trans>
								</strong>
							</p>
							<p>
								<Trans>
									For projects available on the portals channel, they can be projects with an immediate funding need,
									because they can be activated on the spot and start raising funds straight away. They can address more
									varied causes because the donor reaches the donation page and can read more information than when
									making a purchase.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`What is the best way to write the project?`,
					answer: (
						<ul className="ml-5 list-disc space-y-2">
							<li>
								<Trans>Convey clear, concise and direct messages.</Trans>
							</li>
							<li>
								<Trans>
									Get to the point, give a precise idea of what the project is about so the general public can
									understand it.
								</Trans>
							</li>
							<li>
								<Trans>Provide impact data.</Trans>
							</li>
							<li>
								<Trans>Answer each section only with the information that is requested.</Trans>
							</li>
							<li>
								<Trans>
									Write short texts: nowadays, most people consult project information from their phone, so long texts
									take up a lot of space and discourage reading. As for partner companies, the people in charge of
									selecting projects do not have much time to analyze each one either, so they need{' '}
									<strong>brief, direct explanations that catch their eye with impact data.</strong>
								</Trans>
							</li>
						</ul>
					),
				},
				{
					question: t`Is there a maximum number of projects I can submit?`,
					answer: (
						<p>
							<Trans>
								There is no limit, you can submit as many projects as you want. That said, more than quantity we
								recommend prioritizing quality, well-structured and well-written projects that can catch the attention
								of partner companies and potential donors. If your organization works on diverse topics (for example:
								you work with rare diseases and have research projects, leisure and arts activity projects, and labor
								inclusion projects) you can send us projects across the different topics to try to reach more companies
								with different interest areas.
							</Trans>
						</p>
					),
				},
				{
					question: t`What are the different project statuses and what do they mean?`,
					answer: (
						<div className="flex flex-col gap-3">
							<ul className="ml-5 list-disc space-y-2">
								<li>
									<Trans>
										<strong>Not raising</strong>: the project is not receiving donations.
									</Trans>
								</li>
								<li>
									<Trans>
										<strong>Raising</strong>: the project is receiving donations.
									</Trans>
								</li>
							</ul>
							<p>
								<Trans>Only for projects on the portals (Worldcoo Plus):</Trans>
							</p>
							<ul className="ml-5 list-disc space-y-2">
								<li>
									<Trans>
										<strong>Paused</strong>: fundraising for the project, which was receiving donations, has been paused
										for some reason, waiting to resume.
									</Trans>
								</li>
								<li>
									<Trans>
										<strong>Funded</strong>: the project has reached its funding goal or the period during which it was
										receiving donations has ended.
									</Trans>
								</li>
							</ul>
						</div>
					),
				},
				{
					question: t`What visual elements can I add to a project and which formats are supported?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									You can upload a main image of the project (recommended), a video and additional gallery images. All
									media elements support alt text descriptions in each language for accessibility.
								</Trans>
							</p>
							<p>
								<Trans>
									For images, the accepted formats are .jpg, .jpeg, .png, .gif. and they cannot weigh more than 6 MB.
									The recommended aspect ratio is 1200:630 for the main image and 413:286 for the additional images. You
									can upload up to 6 additional images.
								</Trans>
							</p>
							<p>
								<Trans>
									For videos, the accepted formats are .mp4, .webm, .mov and they cannot weigh more than 2 GB.
								</Trans>
								<br />
								<Trans>They can be uploaded as MP4 files or embedded via a URL (YouTube, Vimeo, etc.).</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`When will I be able to start receiving funding for my project?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<strong>
									<Trans>Worldcoo channel</Trans>
								</strong>
							</p>
							<p>
								<Trans>
									The project will be able to start receiving funding once a partner company chooses it to launch a
									fundraising campaign. This may take more or less time and we cannot tell for sure when you will be
									chosen, because it depends on the specific interests of the companies and their final decisions.
								</Trans>
							</p>
							<p>
								<strong>
									<Trans>Worldcoo Plus portals, subscription service</Trans>
								</strong>
							</p>
							<p>
								<Trans>
									For the portals channel, projects can start receiving funding immediately, whenever it suits you best.
									This is a "self-service" funding channel where you create your own campaign and can configure it to go
									live on the day you choose.
								</Trans>
							</p>
						</div>
					),
				},
			],
		},
		{
			id: 'portals',
			title: t`Worldcoo Plus portals`,
			items: [
				{
					question: t`What is Worldcoo Plus and what is a portal?`,
					answer: (
						<p>
							<Trans>
								Worldcoo Plus is a premium service available through subscription that offers you a new funding channel:
								your own solidarity portal, fully customizable, through which you can launch fundraising campaigns
								quickly. Once contracted, this premium service can be easily managed from the NGO Area.
							</Trans>
						</p>
					),
				},
				{
					question: t`How can I subscribe to Worldcoo Plus and activate the portal?`,
					answer: (
						<p>
							<Trans>
								To answer any doubts about subscribing to Worldcoo Plus and the pricing of this premium service, do not
								hesitate to <ContactLink>get in touch with us</ContactLink>. We will be happy to guide you through the
								process.
							</Trans>
							<br />
							<Trans>
								Once the service is contracted, we will create your portal, activate it and give you access so that you
								can personalize it and start adding fundraising campaigns.
							</Trans>
						</p>
					),
				},
				{
					question: t`Is there a maximum number of portals I can have?`,
					answer: (
						<p>
							<Trans>
								You can have as many portals as you want. However, the number of portals will depend on the subscription
								you have. If you want to expand your subscription to increase the number of portals,{' '}
								<ContactLink>contact us</ContactLink>.
							</Trans>
							<br />
							<Trans>
								It can be useful to have more than one portal in order to have unique URLs for specific campaigns,
								topics or moments of the year (example: a Christmas portal, a portal for a solidarity marathon on a
								specific topic, etc.).
							</Trans>
						</p>
					),
				},
				{
					question: t`How do I configure my portal?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									Go to the{' '}
									<Link to="/ngos/portals" className={linkClass}>
										Worldcoo Plus
									</Link>{' '}
									section and open your portal (by clicking on the portal image or on "View details"). You can configure
									its appearance (logo, colors, header image), add a description, set donation amounts, include
									frequently asked questions for donors and add image carousels. At any time you can see how the portal
									looks through the "Preview" button. Any change you make can be seen immediately through that button.
								</Trans>
							</p>
							<p>
								<Trans>
									Each portal and each specific campaign has a URL you can share with whoever you want to promote your
									projects among potential donors.
								</Trans>
								<br />
								<Trans>
									If you have not yet added portals as a funding channel and you want your projects to be able to
									receive donations through this channel, <ContactLink>contact us</ContactLink>.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`Can I edit my portal's URL?`,
					answer: (
						<p>
							<Trans>
								The portal URL is defined at the moment of subscription to the service and is not editable. If you need
								to modify it, please <ContactLink>contact our team</ContactLink>.
							</Trans>
						</p>
					),
				},
				{
					question: t`How do I configure the portal language?`,
					answer: (
						<p>
							<Trans>
								You can configure several languages for your portal: all the content (title, description, testimonials,
								frequently asked questions, etc.) can be translated into each language you enable. The base language by
								default will be the one you have marked as "Default" and the portal content will be translated
								automatically into the rest of the selected languages. You will be able to edit those translations
								later.
							</Trans>
						</p>
					),
				},
				{
					question: t`How do I configure the portal's appearance?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									Inside{' '}
									<Link to="/ngos/portals" className={linkClass}>
										Worldcoo Plus
									</Link>{' '}
									go to "Portal details": here you will be able to configure the portal's appearance. You can include
									the text that will appear in a featured position (Name and introduction), your organization's logo or
									the logo of a specific campaign, a cover image and the color configuration (it lets you choose a main
									color, used for buttons and the background of images, as well as a secondary color, which will be the
									text font color).
								</Trans>
							</p>
							<p>
								<Trans>You can see at any moment how each change looks through the "Preview" button.</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`Can I customize the donation amounts?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									Yes. When you configure the portal you can define default donation amounts that will be shown to
									potential donors. You can also include impact equivalences that help make your project tangible and
									that will appear when the donor selects a specific amount (for example, if you have set that with 5
									EUR a hot meal can be offered, when the donor selects 5 EUR that sentence will appear).
								</Trans>
							</p>
							<p>
								<Trans>
									Users will always have the option to enter the amount they want in a separate field, and inside each
									project specific amounts can be configured. If you do not fill in the specific amounts for each
									project, when the donation is made the generic amounts you configured for the portal will appear.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`How do I assign projects to a portal?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									Open a portal (inside{' '}
									<Link to="/ngos/portals" className={linkClass}>
										Worldcoo Plus
									</Link>{' '}
									click on the portal image or go to "View details") and go to the "Campaigns" tab. From here you can
									click on "New Campaign" and select which existing projects you want to appear on this portal, or
									create a new one.
								</Trans>
							</p>
							<p>
								<Trans>
									If you select an existing project that was only available for Worldcoo, there will be a series of
									fields and information missing — fields specific to the portal — that the tool will flag and ask you
									to fill in (such as the visual header configuration for the portal, the financial goal and the dates
									you want to set for that campaign, etc.). After that, you will be able to launch the fundraising
									campaign.
								</Trans>
							</p>
							<p>
								<Trans>
									When donors enter the portal, they will see the projects you have assigned to it (which will have been
									converted into fundraising campaigns) and will be able to choose which one to support with their
									donations.
								</Trans>
							</p>
						</div>
					),
				},
				{
					question: t`How many fundraising campaigns can I have on the portal?`,
					answer: (
						<p>
							<Trans>
								Inside each portal you can link as many projects as you want: that is, you can have as many fundraising
								campaigns as you want.
							</Trans>
						</p>
					),
				},
				{
					question: t`What actions can I perform on the projects assigned to a campaign on my portal?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									Inside{' '}
									<Link to="/ngos/portals" className={linkClass}>
										Worldcoo Plus
									</Link>{' '}
									click on the image of the portal you want to open or go to "View details" and go to the "Campaigns"
									tab. Here you can perform several actions on each project, clicking on the three dots that appear on
									the right side of each one:
								</Trans>
							</p>
							<ul className="ml-5 list-disc space-y-2">
								<li>
									<Trans>
										<strong>Share</strong>: get a link to share the campaign with anyone and promote it.
									</Trans>
								</li>
								<li>
									<Trans>
										<strong>Edit</strong>: modify the campaign data, the funding goal, the schedule…
									</Trans>
								</li>
								<li>
									<Trans>
										<strong>Duplicate</strong>: create an exact copy of an existing campaign.
									</Trans>
								</li>
								<li>
									<Trans>
										<strong>Pause</strong>: stop funding for the current campaign until you decide to resume donations.
									</Trans>
								</li>
								<li>
									<Trans>
										<strong>Resume</strong>: make a paused campaign start receiving donations again. This action will
										only appear for paused campaigns.
									</Trans>
								</li>
							</ul>
						</div>
					),
				},
				{
					question: t`What visual elements can I add to a portal and which formats are supported?`,
					answer: (
						<div className="flex flex-col gap-3">
							<p>
								<Trans>
									In the "Portal details" section you can upload an organization logo, which will appear at the top left
									of the portal, as well as a cover image, which will be the main image of the portal and will be
									displayed in a featured position.
								</Trans>
							</p>
							<p>
								<Trans>
									In the "Testimonials" section you can also upload an image of the project's testimony. The accepted
									image formats are .jpg, .jpeg, .png, .gif, the recommended dimensions are 1280:840 and they cannot
									weigh more than 6 MB.
								</Trans>
							</p>
							<p>
								<Trans>
									These visual elements are for the portal. Then, each campaign can include different visual elements,
									with the same requirements as for the project images.
								</Trans>
							</p>
						</div>
					),
				},
			],
		},
		{
			id: 'settings',
			title: t`Settings`,
			items: [
				{
					question: t`How do I update my organization's information?`,
					answer: (
						<p>
							<Trans>
								Go to{' '}
								<Link to="/ngos/settings" className={linkClass}>
									Settings
								</Link>{' '}
								and click on the "Organization details" tab. Here you can update your organization name, website, tax
								ID, address, logo, privacy policy URL, scope of action, countries of operation and whether it is
								accredited by an independent body. You can also go to the "Contact person" tab to include and modify the
								details of up to two contact persons and a legal representative. Remember to save your changes before
								leaving. You can update this data at any time to reflect any changes in the organization.
							</Trans>
						</p>
					),
				},
				{
					question: t`How can I give access to the NGO Area to more people in my organization?`,
					answer: (
						<p>
							<Trans>
								Go to{' '}
								<Link to="/ngos/settings" className={linkClass}>
									Settings
								</Link>{' '}
								and click on the "Users with access" section. Once inside, click on the "Add email address" button. You
								can add as many email addresses of people in your organization as you want. Whenever they want to access
								the NGO Area, they will be asked to enter their email address and then a one-time code they will receive
								by email, which will give them access.
							</Trans>
						</p>
					),
				},
				{
					question: t`Can I send you corporate volunteering proposals for companies?`,
					answer: (
						<p>
							<Trans>
								If you tick the box stating that your organization offers corporate volunteering activities (in the{' '}
								<Link to="/ngos/settings" className={linkClass}>
									Settings
								</Link>{' '}
								section, "Corporate volunteering" area), you will have a space to add a brief summary of the type of
								activities you offer. If we ever need to expand information because there is a query or opportunity with
								a company, we will ask you for more details.
							</Trans>
						</p>
					),
				},
				{
					question: t`How is data protection handled?`,
					answer: (
						<p>
							<Trans>
								When a donor requests a donation certificate, they share their personal data with Worldcoo and also with
								the social organizations so the certificates can be issued. That is why it is very important that we
								have the data protection information of each organization. Make sure the information in the "Data
								protection" section (inside{' '}
								<Link to="/ngos/settings" className={linkClass}>
									Settings
								</Link>
								) is up to date.
							</Trans>
						</p>
					),
				},
				{
					question: t`How do I configure my organization's bank details to receive funds from donations?`,
					answer: (
						<p>
							<Trans>
								In{' '}
								<Link to="/ngos/settings" className={linkClass}>
									Settings
								</Link>
								, go to the "Bank details" tab. In this section you can enter your organization's bank details, which
								will be used to receive the payments from funded fundraising campaigns. Here you can edit the bank
								details such as the IBAN or SWIFT code, account holder name and tax address. You can also upload a bank
								ownership certificate. In the event of an update to the IBAN or SWIFT, the new bank ownership
								certificate will have to be uploaded.
							</Trans>
						</p>
					),
				},
			],
		},
	];
}

export type FaqCategory = ReturnType<typeof useFaqCategories>[number];
export type FaqItem = FaqCategory['items'][number];
